import { Calendar, User, Tag, ArrowLeft, Clock, Share2, Phone, Mail, AlertCircle, Scale, BookOpen, ExternalLink } from 'lucide-react'
import { Link } from 'react-router-dom'

export default function DriverHoursRegulations() {
  const shareUrl = window.location.href
  const shareTitle = "Understanding the New Driver Hours Regulations"

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          url: shareUrl,
        })
      } catch (error) {
        console.log('Error sharing:', error)
      }
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-gradient-to-br from-c9-blue to-c9-blue-alt text-white py-16">
        <div className="container-full">
          <div className="flex items-center gap-4 mb-8">
            <Link 
              to="/blog" 
              className="flex items-center gap-2 text-c9-orange hover:text-white transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
              Back to Blog
            </Link>
          </div>
          
          <div className="max-w-4xl">
            <div className="flex flex-wrap items-center gap-4 mb-6 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>5th June 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span>9 min read</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="h-4 w-4" />
                <span>Sam Wharfe (CEO, C9 Recruitment)</span>
              </div>
              <span className="bg-c9-orange px-3 py-1 rounded-full text-xs font-medium">Industry News</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-black mb-6 leading-tight">
              Understanding the New <span className="text-c9-orange">Driver Hours</span> Regulations
            </h1>
            
            <p className="text-xl mb-8 leading-relaxed opacity-90">
              Staying compliant with driver hours regulations is crucial for every professional driver. Here's everything you need to know about the current rules, recent changes, and how to ensure you remain compliant.
            </p>
            
            <div className="flex items-center gap-4">
              <button 
                onClick={handleShare}
                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg transition-colors"
              >
                <Share2 className="h-4 w-4" />
                Share Article
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-16">
        <div className="container-full">
          <div className="article-content">
            <div className="article-prose">
              
              <h2 className="flex items-center gap-3">
                <Scale className="h-6 w-6 text-c9-orange" />
                The Importance of Compliance
              </h2>
              
              <p>Driver hours regulations exist to protect road safety, ensure fair working conditions, and prevent driver fatigue. Non-compliance can result in significant penalties for both drivers and operators, including fines, prohibition notices, and even loss of operating licences.</p>
              
              <p>The rules are governed by EU regulations that continue to apply in the UK post-Brexit, alongside domestic working time regulations. Understanding these rules is not just about legal compliance – it's about protecting your health, safety, and career.</p>

              <div className="article-warning">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-6 w-6 text-yellow-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-bold text-yellow-800 mb-2">Compliance Alert</h3>
                    <p className="text-yellow-700">Recent DVSA enforcement campaigns have shown increased penalties for hours violations. Ensure you understand current regulations to protect your licence and career.</p>
                  </div>
                </div>
              </div>

              <h2>Daily Driving Rules</h2>
              
              <h3>Maximum Daily Driving Time</h3>
              <ul>
                <li><strong>Standard limit:</strong> 9 hours per day</li>
                <li><strong>Extended limit:</strong> 10 hours per day (maximum twice per week)</li>
                <li><strong>Multi-manning:</strong> 10 hours when two qualified drivers are present</li>
              </ul>

              <h3>Continuous Driving Periods</h3>
              <ul>
                <li><strong>Maximum continuous driving:</strong> 4.5 hours</li>
                <li><strong>Required break:</strong> Minimum 45 minutes after 4.5 hours driving</li>
                <li><strong>Split breaks:</strong> Can be taken as 15 minutes followed by 30 minutes</li>
                <li><strong>Break activities:</strong> Must not involve any work-related activities</li>
              </ul>

              <h2>Weekly and Fortnightly Rules</h2>
              
              <h3>Weekly Driving Limits</h3>
              <ul>
                <li><strong>Single week maximum:</strong> 56 hours</li>
                <li><strong>Two-week maximum:</strong> 90 hours total</li>
                <li><strong>Average over four weeks:</strong> Must not exceed 45 hours per week</li>
              </ul>

              <h3>Weekly Rest Periods</h3>
              <ul>
                <li><strong>Regular weekly rest:</strong> 45 hours minimum</li>
                <li><strong>Reduced weekly rest:</strong> 24 hours minimum (must be compensated)</li>
                <li><strong>Compensation:</strong> Must be taken before the third week following reduction</li>
                <li><strong>Two-week rule:</strong> Must have at least one regular weekly rest in any two-week period</li>
              </ul>

              <h2>Daily Rest Requirements</h2>
              
              <h3>Standard Daily Rest</h3>
              <ul>
                <li><strong>Regular daily rest:</strong> 11 hours minimum</li>
                <li><strong>Reduced daily rest:</strong> 9 hours minimum (maximum 3 times between weekly rests)</li>
                <li><strong>Split daily rest:</strong> Can be split into 3 hours + 9 hours</li>
              </ul>

              <h3>Multi-Manning Rules</h3>
              <ul>
                <li><strong>Shared driving:</strong> Each driver needs 9 hours rest in 30-hour period</li>
                <li><strong>First hour:</strong> Can be used for other work</li>
                <li><strong>Ferry/train travel:</strong> Can count as rest if appropriate facilities available</li>
              </ul>

              <div className="article-highlight">
                <div className="flex items-start gap-3">
                  <BookOpen className="h-6 w-6 text-c9-orange mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-bold text-c9-blue mb-2">Planning Insight</h3>
                    <p className="text-gray-700">Always plan your journey with regulations in mind. Factor in break times, potential delays, and ensure you have sufficient time to complete your journey legally.</p>
                  </div>
                </div>
              </div>

              <h2>Working Time Directive</h2>
              
              <p>Separate from EU driving rules, UK Working Time Regulations also apply:</p>
              
              <h3>Working Time Limits</h3>
              <ul>
                <li><strong>Average working week:</strong> 48 hours (calculated over 17-26 weeks)</li>
                <li><strong>Maximum working week:</strong> 60 hours in any single week</li>
                <li><strong>Night work limits:</strong> Average 10 hours per 24-hour period</li>
              </ul>

              <h3>Break and Rest Entitlements</h3>
              <ul>
                <li><strong>Working time break:</strong> 30 minutes after 6 hours work</li>
                <li><strong>Daily rest:</strong> 11 hours in 24-hour period</li>
                <li><strong>Weekly rest:</strong> 24 hours in each 7-day period</li>
              </ul>

              <h2>Tachograph Requirements</h2>
              
              <h3>Digital Tachograph Operation</h3>
              <ul>
                <li><strong>Driver card insertion:</strong> Must insert card before starting work</li>
                <li><strong>Activity recording:</strong> Correctly record all activities throughout shift</li>
                <li><strong>Manual entries:</strong> Record activities during card malfunctions</li>
                <li><strong>Card removal:</strong> Only remove at end of shift or when changing vehicles</li>
              </ul>

              <h3>Record Keeping</h3>
              <ul>
                <li><strong>Driver cards:</strong> Must carry current and previous day's records</li>
                <li><strong>Printouts:</strong> Required for manual entries and certain situations</li>
                <li><strong>Weekly records:</strong> Must be available for current and previous weeks</li>
                <li><strong>Data downloads:</strong> Regular downloads required by operator</li>
              </ul>

              <h2>Exemptions and Special Cases</h2>
              
              <h3>Emergency Situations</h3>
              <ul>
                <li><strong>Road safety:</strong> Departures from rules to ensure safety</li>
                <li><strong>Person safety:</strong> Reaching place of safety in emergencies</li>
                <li><strong>Minimal departures:</strong> Should be kept to absolute minimum</li>
                <li><strong>Compensation:</strong> Equivalent rest must be taken afterwards</li>
              </ul>

              <h3>Specialised Operations</h3>
              <ul>
                <li><strong>Breakdown recovery:</strong> Specific exemptions apply</li>
                <li><strong>Specialist vehicles:</strong> Some construction and utility vehicles exempt</li>
                <li><strong>Emergency services:</strong> Certain emergency response exemptions</li>
              </ul>

              <h2>Penalties and Enforcement</h2>
              
              <h3>Driver Penalties</h3>
              <ul>
                <li><strong>Fixed penalties:</strong> £200-£300 for most infringements</li>
                <li><strong>Court prosecution:</strong> Up to £2,500 fines for serious breaches</li>
                <li><strong>Driving prohibition:</strong> Immediate roadside bans possible</li>
                <li><strong>Licence points:</strong> Endorsements for serious violations</li>
              </ul>

              <h3>Operator Consequences</h3>
              <ul>
                <li><strong>Operator licence action:</strong> Curtailment or revocation possible</li>
                <li><strong>Financial penalties:</strong> Substantial fines for systematic breaches</li>
                <li><strong>Public inquiry:</strong> Called to explain compliance failures</li>
                <li><strong>Reputation damage:</strong> OCRS scoring and industry reputation</li>
              </ul>

              <h2>Best Practice Guidelines</h2>
              
              <h3>Daily Planning</h3>
              <ul>
                <li><strong>Pre-journey planning:</strong> Calculate driving times and required breaks</li>
                <li><strong>Route familiarisation:</strong> Know where suitable break facilities are located</li>
                <li><strong>Buffer time:</strong> Allow extra time for delays and unforeseen circumstances</li>
                <li><strong>Communication:</strong> Keep transport office informed of progress</li>
              </ul>

              <h3>Driver Responsibilities</h3>
              <ul>
                <li>Understanding and complying with all regulations</li>
                <li>Proper use of tachograph equipment</li>
                <li>Taking required breaks and rest periods</li>
                <li>Recording all activities accurately</li>
                <li>Reporting equipment faults and infringements</li>
              </ul>

              <h3>Operator Responsibilities</h3>
              <p>Operators must:</p>
              <ul>
                <li>Organise work to enable compliance with rules</li>
                <li>Monitor drivers' hours regularly</li>
                <li>Provide appropriate training</li>
                <li>Maintain and download tachograph data</li>
                <li>Take corrective action when infringements occur</li>
              </ul>

              <div className="article-success">
                <div className="flex items-start gap-3">
                  <BookOpen className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-bold text-green-800 mb-2">Best Practice</h3>
                    <p className="text-green-700">Always plan conservatively, allowing extra time for delays. It's better to arrive safely and legally than to rush and risk penalties or accidents due to fatigue.</p>
                  </div>
                </div>
              </div>

              <h2>Technology and Apps</h2>
              
              <p>Several mobile apps can help you track your hours:</p>
              <ul>
                <li><strong>Official apps:</strong> Some tachograph manufacturers provide companion apps</li>
                <li><strong>Third-party apps:</strong> Various apps help track hours and plan breaks</li>
                <li><strong>Fleet management systems:</strong> Many operators provide real-time monitoring</li>
                <li><strong>Planning tools:</strong> Route planners that consider driving hours</li>
              </ul>

              <h2>Staying Updated</h2>
              
              <p>Regulations can change, so it's important to:</p>
              <ul>
                <li>Regularly check DVSA guidance and updates</li>
                <li>Attend refresher training courses</li>
                <li>Join professional associations for updates</li>
                <li>Discuss changes with your transport manager</li>
                <li>Use reputable industry publications</li>
              </ul>

              <h2>Getting Help</h2>
              
              <p>If you're unsure about any aspect of drivers' hours rules:</p>
              <ul>
                <li>Consult your transport manager or compliance officer</li>
                <li>Contact DVSA for official guidance</li>
                <li>Seek advice from professional driving organisations</li>
                <li>Attend additional training courses</li>
                <li>Work with recruitment agencies who understand the regulations</li>
              </ul>
              
              <p>At C9 Recruitment, we ensure all our candidates understand their legal obligations and work only with operators who prioritise compliance and driver welfare.</p>

            </div>
          </div>
        </div>
      </section>

      {/* Enhanced CTA Section */}
      <section className="bg-c9-blue text-white section-padding">
        <div className="container-full text-center">
          <div className="max-w-3xl mx-auto">
            <div className="article-cta">
              <h3 className="text-2xl font-bold mb-4 flex items-center justify-center gap-2">
                <Scale className="h-6 w-6" />
                Find Compliant Employers
              </h3>
              <p className="text-lg mb-6">
                Looking for employers who prioritise legal compliance and driver welfare? We work exclusively with operators who understand the importance of proper hours management and regulatory compliance.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  to="/candidates" 
                  className="flex items-center justify-center gap-2 bg-white text-c9-orange hover:bg-gray-100 px-8 py-3 rounded-lg font-semibold transition-colors"
                >
                  <ExternalLink className="h-5 w-5" />
                  Browse Compliant Opportunities
                </Link>
                <a 
                  href="tel:02081524574" 
                  className="flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white hover:bg-white hover:text-c9-blue px-8 py-3 rounded-lg font-medium transition-colors"
                >
                  <Phone className="h-5 w-5" />
                  Speak to Compliance Experts
                </a>
              </div>
              
              <div className="mt-8 text-sm text-gray-200">
                <p>Connect with operators who value regulatory compliance and driver welfare</p>
                <div className="flex items-center justify-center gap-6 mt-3">
                  <a href="tel:02081524574" className="flex items-center gap-2 hover:text-white transition-colors">
                    <Phone className="h-4 w-4" />
                    0208 152 4574
                  </a>
                  <a href="mailto:commercial@c9-recruitment.com" className="flex items-center gap-2 hover:text-white transition-colors">
                    <Mail className="h-4 w-4" />
                    commercial@c9-recruitment.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
